import capitalize from 'lodash/capitalize';
import partition from 'lodash/partition';

import {reporter} from 'common/report/error-reporting';

import {DataDefinitionsClient} from './data-definitions-client';

class PropertyDefinitionsClient extends DataDefinitionsClient {
  public arrayFormatBracket = true;

  buildEndpoint({projectId, workspaceId}) {
    return `${super.buildEndpoint({projectId, workspaceId})}/properties`;
  }

  /**
   * If category is defined, event is not, and vice-versa.
   *
   * Category has two possible values:
   *   user: grab user properties
   *   event: grab event properties
   *   super: grab properties present on the majority of events
   */
  buildQueryParams(
    options: {
      id?: number;
      name?: string;
      category?: string;
      event?: string;
      includeCustom?: boolean;
      useQueryCache?: boolean;
    } = {},
  ) {
    const {id, name, category, event, includeCustom = false, useQueryCache = null} = options;
    // "events" is a common mistake
    // the API is case sensitive, but our frontend javascript feeds in lowercase values
    const resourceType = category === `events` ? `Event` : capitalize(category) || undefined;
    return {
      id,
      name,
      resourceType,
      event,
      includeCustom,
      ...(useQueryCache !== null ? {use_query_cache: useQueryCache} : {}),
    };
  }

  buildRequestBody({propertyDefinition}) {
    return JSON.stringify(propertyDefinition);
  }

  processResponse(response, params) {
    const result = super.processResponse(response, params);
    // the API is case sensitive, but our frontend javascript expects lowercase values
    // TODO: fix the inconsistency with `events` and `event` on the param.category
    // use `user` for now
    const resourceType = params.category === `user` ? `user` : `event`;
    if (Array.isArray(result)) {
      // GOOT for projects with bad user properties see: https://mixpanel.slack.com/archives/C07KZ4NBB/p1605105643087200
      const [valid, invalid] = partition(result, (i) => i.name);
      if (invalid.length > 0) {
        reporter.warn(`unexpected property-definitions for ${resourceType}`, invalid);
      }
      return [...valid.map((def) => ({...def, resourceType}))];
    } else {
      return {...result, resourceType: result.resourceType.toLowerCase()};
    }
  }
}

export const propertyDefinitionsClient = new PropertyDefinitionsClient();
